import { ButtonHTMLAttributes, ReactNode } from "react";
import { Button as ButtonPrimitive } from "@radix-ui/themes";
import { Loader2 } from "lucide-react";
import { cn } from "@/utils/cn";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "outline"
  | "outlineBlue"
  | "primaryGradient"
  | "inviteGradient"
  | "outlinePrimary"
  | "accent"
  | "pillGradient"
  | "secondaryOutline"
  | "outlineWarm"
  | "linkedin"
  | "googleOutline";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  onClick?: () => void;
}

export const Button = ({
  children,
  variant = "primary",
  size = "lg",
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className = "",
  disabled,
  onClick,
}: ButtonProps) => {
  const baseClasses = "btn";

  const variantClasses = {
    primary: "btn-primary",
    secondary: "btn-secondary",
    ghost: "btn-ghost",
    danger: "btn-danger",
    outline: "btn-outline",
    outlineBlue: "border border-primary-650 text-primary-650 bg-white",
    primaryGradient: "bg-primary-gradient",
    inviteGradient: "bg-pub-invite-gradient text-white hover:opacity-90 shadow-[0px_1px_1.5px_rgba(0,0,0,0.1)]",
    outlinePrimary:
      "border border-theme-btn-outline-primary-border bg-theme-btn-outline-primary-bg text-theme-btn-outline-primary-text",
    accent: "border border-[#95A0FF] bg-[#EEF2FF] text-slate-900 hover:bg-[#e0e7ff]",
    pillGradient:
      "bg-pillWarm-gradient border border-pillWarm-650 text-pillWarm-650 hover:opacity-80",
    secondaryOutline: "bg-primary-250 border border-primary-350 text-secondary-purple",
    outlineWarm:
      "border border-pillWarm-200 bg-pillWarm-50 text-pillWarm-750 hover:bg-pillWarm-100",
    linkedin:
      "bg-linkedin text-white hover:opacity-90 rounded-[14px] shadow-[0px_10px_15px_0px_rgba(10,102,194,0.2),0px_4px_6px_0px_rgba(10,102,194,0.2)]",
    googleOutline:
      "bg-white border border-secondary-200 text-secondary-900 hover:bg-neutral-50 rounded-[14px] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08),0px_1px_4px_0px_rgba(0,0,0,0.05)]",
  };

  const sizeClasses = {
    sm: "px-6 py-3 text-xs",
    md: "px-8 py-4 text-sm",
    lg: "px-10 py-5 text-base",
  };

  const widthClasses = fullWidth ? "w-full" : "";

  return (
    <ButtonPrimitive
      className={cn(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        widthClasses,
        className,
      )}
      disabled={disabled || isLoading}
      onClick={onClick}
    >
      {isLoading && <Loader2 className="mr-4 h-8 w-8 animate-spin" />}
      {!isLoading && leftIcon && <span className="mr-3">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span className="ml-4">{rightIcon}</span>}
    </ButtonPrimitive>
  );
};

Button.displayName = "Button";

export default Button;
